import timeSync from '@/infrastructure/utils/timeSync';
import { DateTime } from 'luxon';
/**
 * Insert date labels and unread separators into a list of messages.
 * @param {Array} messages - List of messages (Newest -> Oldest).
 */
export const insertDateLabels = messages => {
   if (!messages || messages.length === 0) return [];

   // 1. Filter out existing date labels to ensure idempotency
   const rawMessages = messages.filter(msg => msg.type !== 'date-label');
   const result = [];

   const getDateKey = msg => {
      if (msg.type !== 'message' && !msg.chatType) return null;

      let dt;
      if (msg.messageTime) {
         dt = DateTime.fromMillis(msg.messageTime);
      } else if (msg.createdAt) {
         dt = DateTime.fromSQL(msg.createdAt);
      }

      if (!dt?.isValid) return null;
      return dt.toISODate();
   };

   const getLabel = dateKey => {
      const msgDate = DateTime.fromISO(dateKey);
      const today = timeSync.getSyncedNow().startOf('day');

      if (msgDate.hasSame(today, 'day')) return 'Today';
      if (msgDate.hasSame(today.minus({ days: 1 }), 'day')) return 'Yesterday';
      return msgDate.toLocaleString({ month: 'long', day: '2-digit', year: 'numeric' });
   };

   // Traverse in order (newest → oldest)
   for (let i = 0; i < rawMessages.length; i++) {
      const msg = rawMessages[i];
      const dateKey = getDateKey(msg);
      result.push({ ...msg, type: msg.type ?? 'message' });

      // 🏷️ If this item has a date, check if we need to insert a label after it
      if (dateKey) {
         // Find the next message that has a date key to compare
         let nextDateKey = null;
         for (let j = i + 1; j < rawMessages.length; j++) {
            const nextMsg = rawMessages[j];
            const nextKey = getDateKey(nextMsg);
            if (nextKey) {
               nextDateKey = nextKey;
               break;
            }
         }

         if (dateKey !== nextDateKey) {
            result.push({
               type: 'date-label',
               label: getLabel(dateKey),
               dateKey,
               msgId: `${msg.msgId}-date-${dateKey}`,
            });
         }
      }
   }

   return result;
};

/**
 * Deduplicates a list of messages based on msgId.
 * Preserves the first occurrence (useful for newest-first lists).
 * @param {Array} messages
 * @returns {Array}
 */
export const deduplicateMessages = messages => {
   if (!messages?.length) return [];
   const seen = new Set();
   return messages.filter(msg => {
      const id = msg.type === 'date-label' ? `label-${msg.dateKey}` : msg.msgId;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
   });
};

export const getMessageSenderId = ({ type, message }) => {
   if (type === 'chats') {
      if (message.chatType === 'chat') return message.fromUserId;
      if (message.chatType === 'groupchat') return message.publisherId;
   }
   return '';
};
